import dayjs from 'dayjs';
import type { ProgramType } from '../../interfaces/CamProgramming.interface';

/** True when editor HTML has visible text or an inline image. */
export const hasRichTextContent = (html: string | null | undefined) =>
	!!html && (!!html.replace(/<[^>]*>/g, '').trim() || html.includes('<img'));

/** Pull a field-level API validation message when present. */
export const programApiErrorMessage = (
	error: unknown,
	fallback: string
): string => {
	if (!error || typeof error !== 'object' || !('data' in error)) {
		return fallback;
	}

	const data = (error as { data?: Record<string, unknown> }).data;
	if (!data) {
		return fallback;
	}

	// Field errors first: they say what is actually wrong, while `detail` on
	// the same response is usually the generic wrapper around them.
	for (const field of ['filename', 'order_line_items', 'order_line_item']) {
		const fieldError = data[field];
		if (fieldError) {
			return Array.isArray(fieldError)
				? String(fieldError[0])
				: String(fieldError);
		}
	}

	if (data.detail) {
		return String(data.detail);
	}

	return fallback;
};

const PROGRAM_LINKING_EXPLANATION: Record<ProgramType, string> = {
	cam:
		'The same part is often ordered more than once on a single order (batches). If the material and surface finish are the same, you can use the same program for all the part batches on an order. Check all the line items below that use the same program you created.',
	keyence:
		'The same part is often ordered more than once on a single order (batches). You can use the same program for all matching part batches on an order, even when material or surface finish differs. Check all the line items below that should use the same program you created.',
	inspection_plan:
		'The same part is often ordered more than once on a single order (batches). You can use the same program for all matching part batches on an order, even when material or surface finish differs. Check all the line items below that should use the same program you created.',
};

/** Explains why linking exists and what the programmer should do. */
export const programLinkingExplanation = (programType: ProgramType) =>
	PROGRAM_LINKING_EXPLANATION[programType];

const formatProgramDueDate = (value: string | null | undefined) =>
	value ? dayjs(value).format('MMM D, YYYY') : '—';

/** Order number, quantity, and due date for grid/table part subtitles. */
export const formatOrderLineItemSubtitle = (orderLineItem: {
	order_name?: string | null;
	quantity?: number | null;
	required_part_ship_date?: string | null;
}) => {
	const parts = [
		orderLineItem.order_name?.trim() || '—',
		`Qty ${orderLineItem.quantity ?? '—'}`,
		formatProgramDueDate(orderLineItem.required_part_ship_date),
	];
	return parts.join(' · ');
};
